import { InspectorControls, useBlockProps } from "@wordpress/block-editor";
import {
    PanelBody,
    RangeControl,
    CheckboxControl,
    ColorPicker,
    TabPanel,
    Button,
    ButtonGroup,
    __experimentalView as View,
    __experimentalText as Text,
    Flex,
    FlexItem,
} from "@wordpress/components";
import { desktop, mobile, tablet, smartphone } from "@wordpress/icons";
import React from "react";

const { addFilter } = wp.hooks;

// Add custom attribute
addFilter(
    "blocks.registerBlockType",
    "danone/grid-mobile-breakpoint",
    (settings, name) => {
        if (name !== "core/group") {
            return settings;
        }

        return {
            ...settings,
            attributes: {
                ...settings.attributes,
                changeColorOnScroll: {
                    type: "string",
                    default: "",
                },
                brDesktopColumns: {
                    type: "number",
                    default: 0,
                },
                brTabletColumns: {
                    type: "number",
                    default: 0,
                },
                tabletFlexVertical: {
                    type: "boolean",
                    default: false,
                },
                brPhoneColumns: {
                    type: "number",
                    default: 0,
                },
                phoneFlexVertical: {
                    type: "boolean",
                    default: false,
                },
                gapDesktop: {
                    type: "string",
                    default: "",
                },
                gapTablet: {
                    type: "string",
                    default: "",
                },
                gapMobile: {
                    type: "string",
                    default: "",
                },
            },
            save: function (props) {
                const blockProps = useBlockProps.save();
                const { attributes } = props;

                if (attributes.brTabletColumns) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--tablet-grid-columns": `${attributes.brTabletColumns};`,
                    };
                }
                if (attributes.brPhoneColumns) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--phone-grid-columns": `${attributes.brPhoneColumns};`,
                    };
                }
                if (attributes.changeColorOnScroll) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--animation-color": `${attributes.changeColorOnScroll};`,
                    };
                }
                if (attributes.gapDesktop) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--desktop-gap": attributes.gapDesktop,
                    };
                }
                if (attributes.gapTablet) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--tablet-gap": attributes.gapTablet,
                    };
                }
                if (attributes.gapMobile) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--mobile-gap": attributes.gapMobile,
                    };
                }

                // Dodaj style wymiarów
                if (attributes.widthDesktop?.value) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--width-desktop": `${attributes.widthDesktop.value}${
                            attributes.widthDesktop.valueUnit || "px"
                        }`,
                    };
                }
                if (attributes.heightDesktop?.value) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--height-desktop": `${attributes.heightDesktop.value}${
                            attributes.heightDesktop.valueUnit || "px"
                        }`,
                    };
                }
                if (attributes.widthTablet?.value) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--width-tablet": `${attributes.widthTablet.value}${
                            attributes.widthTablet.valueUnit || "px"
                        }`,
                    };
                }
                if (attributes.heightTablet?.value) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--height-tablet": `${attributes.heightTablet.value}${
                            attributes.heightTablet.valueUnit || "px"
                        }`,
                    };
                }
                if (attributes.widthMobile?.value) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--width-mobile": `${attributes.widthMobile.value}${
                            attributes.widthMobile.valueUnit || "px"
                        }`,
                    };
                }
                if (attributes.heightMobile?.value) {
                    blockProps.style = {
                        ...blockProps.style,
                        "--height-mobile": `${attributes.heightMobile.value}${
                            attributes.heightMobile.valueUnit || "px"
                        }`,
                    };
                }

                blockProps.className = `${blockProps.className}
                    ${
                        attributes.gapDesktop ||
                        attributes.gapTablet ||
                        attributes.gapMobile
                            ? "has-custom-gap"
                            : ""
                    }`.trim();

                const originalSaveElement = settings.save(props);
                return React.cloneElement(originalSaveElement, blockProps);
            },
        };
    }
);

const GridTabPanel = ({ attributes, setAttributes }) => {
    const handleReset = (device) => {
        if (device === "desktop") {
            setAttributes({
                layout: {
                    ...attributes.layout,
                    columnCount: 0,
                },
            });
        } else {
            const attributeName =
                device === "tablet" ? "brTabletColumns" : "brPhoneColumns";
            setAttributes({ [attributeName]: 0 });

            const flexAttributeName =
                device === "tablet"
                    ? "tabletFlexVertical"
                    : "phoneFlexVertical";
            setAttributes({ [flexAttributeName]: false });
        }
    };

    return (
        <View
            style={{
                marginBottom: "10px",
            }}
        >
            <TabPanel
                className="custom-settings-tab spacing-device-tabs"
                activeClass="active-tab"
                tabs={[
                    {
                        name: "desktop",
                        title: "Desktop",
                        icon: desktop,
                    },
                    {
                        name: "tablet",
                        title: "Tablet",
                        icon: tablet,
                    },
                    {
                        name: "mobile",
                        title: "Mobile",
                        icon: mobile,
                    },
                ]}
            >
                {(tab) => {
                    const isDesktop = tab.name === "desktop";
                    const isTablet = tab.name === "tablet";
                    const isMobile = tab.name === "mobile";

                    const currentValue = isDesktop
                        ? attributes.layout?.columnCount
                        : isTablet
                        ? attributes.brTabletColumns
                        : attributes.brPhoneColumns;

                    const flexValue = isTablet
                        ? attributes.tabletFlexVertical
                        : attributes.phoneFlexVertical;

                    return (
                        <>
                            <Text
                                variant="subtitle"
                                as="h3"
                                className="tab-title"
                            >
                                Grid Settings
                            </Text>
                            <View
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                }}
                            >
                                <Text
                                    as="button"
                                    variant="link"
                                    onClick={() => handleReset(tab.name)}
                                    style={{
                                        color: "#007cba",
                                        cursor: "pointer",
                                        border: "none",
                                        background: "none",
                                        padding: 0,
                                        textDecoration: "underline",
                                    }}
                                >
                                    Reset
                                </Text>
                            </View>
                            <View style={{ padding: "10px 0" }}>
                                <RangeControl
                                    label={`${
                                        isDesktop
                                            ? "Desktop"
                                            : isTablet
                                            ? "Tablet"
                                            : "Mobile"
                                    } Columns`}
                                    value={
                                        isDesktop
                                            ? attributes.layout?.columnCount
                                            : currentValue
                                    }
                                    onChange={(value) =>
                                        isDesktop
                                            ? setAttributes({
                                                  layout: {
                                                      ...attributes.layout,
                                                      columnCount: value,
                                                  },
                                              })
                                            : setAttributes({
                                                  [isTablet
                                                      ? "brTabletColumns"
                                                      : "brPhoneColumns"]:
                                                      value,
                                              })
                                    }
                                    min={0}
                                    max={12}
                                />
                                <RangeControl
                                    label={`${
                                        isDesktop
                                            ? "Desktop"
                                            : isTablet
                                            ? "Tablet"
                                            : "Mobile"
                                    } Gap`}
                                    value={
                                        parseInt(
                                            attributes[
                                                `gap${
                                                    tab.name
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                    tab.name.slice(1)
                                                }`
                                            ]
                                        ) || 0
                                    }
                                    onChange={(value) =>
                                        setAttributes({
                                            [`gap${
                                                tab.name
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                tab.name.slice(1)
                                            }`]: value ? value + "px" : "",
                                        })
                                    }
                                    min={0}
                                    max={100}
                                />
                                {(isTablet || isMobile) && (
                                    <CheckboxControl
                                        label={`Wrap on ${
                                            isTablet ? "tablet" : "mobile"
                                        }`}
                                        checked={flexValue}
                                        onChange={(value) =>
                                            setAttributes({
                                                [isTablet
                                                    ? "tabletFlexVertical"
                                                    : "phoneFlexVertical"]:
                                                    value,
                                            })
                                        }
                                    />
                                )}
                            </View>
                        </>
                    );
                }}
            </TabPanel>
        </View>
    );
};

// Zmodyfikuj withMobileBreakpoint, usuwając DimensionsControls z renderowania
const withMobileBreakpoint = wp.compose.createHigherOrderComponent(
    (BlockEdit) => {
        return (props) => {
            const { attributes, setAttributes, name, clientId } = props;

            if (name !== "core/group") {
                return <BlockEdit {...props} />;
            }

            const editorStyles = `
                #block-${clientId} {
                    ${
                        attributes.layout?.columnCount === 1
                            ? `display: flex !important; flex-direction: column !important;`
                            : ""
                    }
                    
                    ${
                        attributes.gapDesktop
                            ? `gap: ${attributes.gapDesktop} !important;`
                            : ""
                    }
                    
                    @media (max-width: 1024px) and (min-width: 768px) {
                        ${
                            attributes.brTabletColumns === 1
                                ? `display: flex !important; flex-direction: column !important;`
                                : attributes.brTabletColumns
                                ? `display: grid !important; grid-template-columns: repeat(${attributes.brTabletColumns}, minmax(0, 1fr)) !important;`
                                : ""
                        }
                        ${
                            attributes.gapTablet
                                ? `gap: ${attributes.gapTablet} !important;`
                                : ""
                        }
                    }
                    
                    @media (max-width: 767px) {
                        ${
                            attributes.brPhoneColumns === 1
                                ? `display: flex !important; flex-direction: column !important;`
                                : attributes.brPhoneColumns
                                ? `display: grid !important; grid-template-columns: repeat(${attributes.brPhoneColumns}, minmax(0, 1fr)) !important;`
                                : ""
                        }
                        ${
                            attributes.gapMobile
                                ? `gap: ${attributes.gapMobile} !important;`
                                : ""
                        }
                    }
                    ${
                        attributes.changeColorOnScroll
                            ? `--animation-color: ${attributes.changeColorOnScroll};`
                            : ""
                    }
                }
            `;

            const renderGridControls = () => (
                <PanelBody title="Grid Settings" initialOpen={false}>
                    <GridTabPanel
                        attributes={attributes}
                        setAttributes={setAttributes}
                    />
                </PanelBody>
            );

            const renderFlexControls = () => (
                <PanelBody title="Flex Settings" initialOpen={false}>
                    <GridTabPanel
                        attributes={attributes}
                        setAttributes={setAttributes}
                    />
                </PanelBody>
            );

            const renderAnimationControls = () => (
                <PanelBody title="Animation" initialOpen={false}>
                    <ColorPicker
                        color={attributes.changeColorOnScroll}
                        onChangeComplete={(color) =>
                            setAttributes({ changeColorOnScroll: color.hex })
                        }
                    />
                </PanelBody>
            );

            return (
                <>
                    <style>{editorStyles}</style>
                    <BlockEdit {...props} />
                    <InspectorControls>
                        {attributes.layout?.type === "grid" &&
                            renderGridControls()}
                        {attributes.layout?.type === "flex" &&
                            attributes.layout?.orientation !== "vertical" &&
                            renderFlexControls()}
                        {renderAnimationControls()}
                    </InspectorControls>
                </>
            );
        };
    },
    "withMobileBreakpoint"
);

addFilter(
    "editor.BlockEdit",
    "danone/with-mobile-breakpoint",
    withMobileBreakpoint
);

// Add custom classes
addFilter(
    "blocks.getSaveContent.extraProps",
    "danone/grid-mobile-classes",
    (extraProps, blockType, attributes) => {
        if (blockType.name !== "core/group") {
            return extraProps;
        }

        if (attributes.layout?.type === "grid") {
            if (attributes.layout?.columnCount === 1) {
                extraProps.className = extraProps.className
                    ? `${extraProps.className} is-single-column`
                    : `is-single-column`;
            }

            if (attributes.brTabletColumns === 1) {
                extraProps.className = extraProps.className
                    ? `${extraProps.className} is-tablet-single-column`
                    : `is-tablet-single-column`;
            } else if (attributes.brTabletColumns) {
                extraProps.className = extraProps.className
                    ? `${extraProps.className} has-custom-tablet-columns`
                    : `has-custom-tablet-columns`;
            }

            if (attributes.brPhoneColumns === 1) {
                extraProps.className = extraProps.className
                    ? `${extraProps.className} is-mobile-single-column`
                    : `is-mobile-single-column`;
            } else if (attributes.brPhoneColumns) {
                extraProps.className = extraProps.className
                    ? `${extraProps.className} has-custom-phone-columns`
                    : `has-custom-phone-columns`;
            }
        } else if (
            attributes.layout?.type === "flex" &&
            attributes.layout?.orientation !== "vertical"
        ) {
            if (attributes.tabletFlexVertical) {
                extraProps.className = extraProps.className
                    ? `${extraProps.className} is-tablet-flex-vertical`
                    : `is-tablet-flex-vertical`;
            }

            if (attributes.phoneFlexVertical) {
                extraProps.className = extraProps.className
                    ? `${extraProps.className} is-phone-flex-vertical`
                    : `is-phone-flex-vertical`;
            }
        }

        if (attributes.changeColorOnScroll) {
            extraProps.className = extraProps.className
                ? `${extraProps.className} has-color-on-scroll`
                : `has-color-on-scroll`;
        }

        return extraProps;
    }
);
